import { fillSegments, nodeOffset, type Milestone } from './milestoneHelpers'
import MilestoneNode from './MilestoneNode'

/**
 * MilestoneProgressBar — a thin track with circular goal nodes sitting on it.
 *
 * Each milestone marks a goal along the track. The segment of track between
 * two milestones is painted with the gradient of the milestone it leads INTO
 * (`segmentGradient`), falling back to the default green. This keeps the
 * number of fill segments structurally tied to the number of goals, so bars
 * with 2, 3, or more goals need no component changes.
 */

interface MilestoneProgressBarProps {
  id?: string
  title?: string
  /** Current progress value, in the same units as milestone positions. */
  value: number
  /** Track maximum — normally the last milestone's position. */
  max: number
  milestones: Milestone[]
}

export default function MilestoneProgressBar({ id, title, value, max, milestones }: MilestoneProgressBarProps) {
  // fillSegments sorts internally; node positions are absolute, so the map
  // below doesn't need a sorted array.
  const segments = fillSegments(milestones, value, max)

  return (
    <div id={id ?? 'milestone-progress-bar'}>
      {title && <h3 className='text-cta text-center text-xl'>{title}</h3>}

      {/*
        `--node-r` is the node radius (size-11 → 22px, md:size-13.5 → 27px).
        The track spans the full width so it lines up with the labels above
        and below. Node centres — and the fill ends that must meet them — are
        placed with `nodeOffset()`, which insets the usable span by the radius
        so the first/last nodes sit flush with the track ends without the
        track ever extending past them. Sharing one formula keeps the fill and
        nodes aligned at every value and screen width.
      */}
      <div className='relative h-18 mx-2 md:mx-0 [--node-r:22px] md:[--node-r:27px]'>
        {/* track */}
        <div className='bg-track absolute inset-x-0 top-8 h-2 rounded-full'>
          {segments.map((segment, i) => (
            <div
              key={i}
              className='absolute top-0 h-full rounded-full'
              style={{
                // First segment starts at the true left edge so bars without a
                // start node still fill to the edge; later segments start at
                // the previous node centre.
                left: i === 0 ? '0' : nodeOffset(segment.startFrac),
                right: `calc(100% - (${nodeOffset(segment.endFrac)}))`,
                background: `linear-gradient(90deg, ${segment.gradient[0]}, ${segment.gradient[1]})`,
              }}
            />
          ))}
        </div>

        {milestones.map((milestone) => (
          <MilestoneNode
            key={`${milestone.variant}-${milestone.position}`}
            milestone={milestone}
            value={value}
            max={max}
          />
        ))}
      </div>
    </div>
  )
}
